const StyleF = () => {
  return (
    <div className="style-f">
      <style>{`
        .style-f {
          --bg-primary: #ffffff;
          --bg-secondary: #fafafa;
          --bg-accent: #f5f5f5;
          --text-primary: #0f0f0f;
          --text-secondary: #404040;
          --text-tertiary: #666666;
          --accent-primary: #1a1a1a;
          --accent-secondary: #333333;
          --border-main: #d0d0d0;
          --border-subtle: #e8e8e8;
          --shadow: rgba(0, 0, 0, 0.05);
        }
        
        .style-f.dark {
          --bg-primary: #fefefe;
          --bg-secondary: #f8f8f8;
          --bg-accent: #f2f2f2;
          --text-primary: #000000;
          --text-secondary: #2a2a2a;
          --text-tertiary: #555555;
          --accent-primary: #000000;
          --accent-secondary: #1f1f1f;
          --border-main: #c5c5c5;
          --border-subtle: #dedede;
          --shadow: rgba(0, 0, 0, 0.08);
        }

        .style-f {
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          line-height: 1.55;
          letter-spacing: -0.011em;
        }
        
        .style-f h1 {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 2.75rem;
          line-height: 1.15;
          margin-bottom: 1.25rem;
          color: var(--text-primary);
          letter-spacing: -0.02em;
        }
        
        .style-f h2 {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 1.875rem;
          line-height: 1.25;
          margin-bottom: 1rem;
          color: var(--text-primary);
          letter-spacing: -0.015em;
        }
        
        .style-f h3 {
          font-family: 'Inter', sans-serif;
          font-weight: 500;
          font-size: 1.125rem;
          margin-bottom: 0.5rem;
          color: var(--text-secondary);
          letter-spacing: -0.01em;
        }
        
        .style-f .adaptive-container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 2rem;
        }
        
        .style-f .professional-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 8px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px var(--shadow);
          transition: all 0.15s ease;
          position: relative;
        }
        
        .style-f .professional-card:hover {
          border-color: var(--border-main);
          box-shadow: 0 4px 12px var(--shadow);
        }
        
        .style-f .professional-card::after {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: var(--accent-primary);
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        
        .style-f .professional-card:hover::after {
          opacity: 1;
        }
        
        .style-f .context-section {
          margin: 3rem 0;
          padding: 2.5rem 0;
        }
        
        .style-f .flexible-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin: 2rem 0;
        }
        
        .style-f .adaptive-text {
          font-size: 1rem;
          color: var(--text-secondary);
          font-weight: 400;
        }
        
        .style-f .professional-button {
          background: var(--accent-primary);
          color: var(--bg-primary);
          border: 1px solid var(--accent-primary);
          padding: 0.75rem 1.5rem;
          border-radius: 5px;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.15s ease;
          letter-spacing: -0.005em;
        }
        
        .style-f .professional-button:hover {
          background: var(--accent-secondary);
          border-color: var(--accent-secondary);
        }
        
        .style-f .context-aware {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 2.5rem;
          align-items: start;
        }
        
        .style-f .sidebar-highlight {
          background: var(--bg-accent);
          border: 1px solid var(--border-main);
          border-radius: 6px;
          padding: 1.5rem;
        }
        
        .style-f .professional-emphasis {
          color: var(--accent-secondary);
          font-weight: 500;
          position: relative;
        }
        
        .style-f .professional-emphasis::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--border-main);
        }
        
        .style-f .modular-section {
          padding: 2rem;
          margin: 2rem 0;
          border: 1px solid var(--border-subtle);
          border-radius: 8px;
          background: var(--bg-primary);
        }
        
        .style-f .capability-list {
          list-style: none;
          padding: 0;
          margin: 1rem 0;
        }
        
        .style-f .capability-list li {
          padding: 0.375rem 0;
          color: var(--text-secondary);
          font-size: 0.95rem;
          display: flex;
          align-items: center;
        }
        
        .style-f .capability-list li::before {
          content: '•';
          color: var(--accent-primary);
          font-weight: bold;
          width: 1rem;
          margin-right: 0.5rem;
        }
        
        @media (max-width: 768px) {
          .style-f .context-aware {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .style-f .adaptive-container {
            padding: 0 1.5rem;
          }
        }
      `}</style>
      
      <div className="adaptive-container py-12">
        <header className="text-center context-section">
          <h1>Flexible Professional</h1>
          <p className="adaptive-text max-w-2xl mx-auto mt-4">
            Adaptable design that maintains <span className="professional-emphasis">professional credibility</span> across 
            different contexts — from academic conferences to public exhibitions, 
            from grant applications to community outreach.
          </p>
        </header>

        <section className="context-section">
          <div className="professional-card">
            <h2>Context-Aware Design</h2>
            <div className="context-aware">
              <div className="sidebar-highlight">
                <h3>Adaptive Qualities</h3>
                <ul className="capability-list">
                  <li>Scales gracefully across formats</li>
                  <li>Professional in any setting</li>
                  <li>Clear without being clinical</li>
                  <li>Authoritative yet approachable</li>
                </ul>
              </div>
              <div>
                <p className="adaptive-text mb-4">
                  This approach recognizes that cultural institutions operate in multiple contexts. 
                  The same organization might need to speak to funding bodies, academic peers, 
                  and the general public — sometimes all in the same day.
                </p>
                <p className="adaptive-text">
                  Our flexible professional aesthetic maintains <span className="professional-emphasis">dignified consistency</span> 
                  while allowing content and context to take center stage. Clean structure provides 
                  the foundation, while thoughtful details add personality without distraction.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="context-section">
          <h2 style={{textAlign: 'center', marginBottom: '2rem'}}>Design System</h2>
          
          <div className="flexible-grid">
            <div className="modular-section">
              <h3>Modular Typography</h3>
              <p className="adaptive-text">
                Font weights and sizes that work equally well in presentations, 
                reports, and web interfaces. Clear hierarchy without being rigid.
              </p>
            </div>
            
            <div className="modular-section">
              <h3>Flexible Layouts</h3>
              <p className="adaptive-text">
                Grid systems that adapt to content needs while maintaining 
                visual consistency across different media and contexts.
              </p>
            </div>
            
            <div className="modular-section">
              <h3>Subtle Accents</h3>
              <p className="adaptive-text">
                Understated design details that add sophistication without 
                overwhelming the primary message or content.
              </p>
            </div>
            
            <div className="modular-section">
              <h3>Professional Polish</h3>
              <p className="adaptive-text">
                Refined details and careful spacing that signal competence 
                and attention to detail without being flashy.
              </p>
            </div>
          </div>
        </section>

        <section className="context-section">
          <div className="professional-card text-center">
            <h2 style={{marginBottom: '1rem'}}>Trusted & Versatile</h2>
            <p className="adaptive-text max-w-2xl mx-auto mb-6">
              Ideal for institutions that need one cohesive identity to work across academic papers, 
              public presentations, digital platforms, and funding applications. Professional 
              without being stuffy, flexible without losing authority.
            </p>
            <button className="professional-button">
              See the Flexibility
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StyleF;